import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
// import { FaGithub, FaColumns, FaFileExport } from 'react-icons/fa';
import { FaBook, FaCode, FaComments, FaGithub, FaCreativeCommons, FaColumns, FaFileExport } from 'react-icons/fa';
import Header from './Header';

const GET_PUBLICATIONS = gql`
  query {
    publicationsExp (order: "-year"){
      edges {
        node {
          id
          pubId
          doi
          title
          authors
          journal
          number
          pages
          year
        }
      }
    }
  }
`;

const PublicationsPage = () => {
  const { loading, error, data } = useQuery(GET_PUBLICATIONS);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [searchTerms, setSearchTerms] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    year: '',
    journal: '',
    pubId: ''
  });
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    pubId: true,
    doi: true,
    title: true,
    authors: true,
    journal: true,
    year: true
  });
  const formatAuthors = (authors) => {
    if (!authors) return '';
    
    const cleanName = (name) => {
      return name
        .replace(/[\[\]"]/g, '') // Remove brackets and quotes
        .replace(/\\u00f3/g, 'ó') // Replace \u00f3 with ó
        .replace(/\\u00e9/g, 'é') // Replace \u00e9 with é
        .replace(/\\u00f1/g, 'ñ') // Replace \u00f1 with ñ
        .replace(/\\u00e1/g, 'á') // Replace \u00e1 with á
        .replace(/\\u00ed/g, 'í') // Replace \u00ed with í
        .replace(/\\u00ed/g, 'í') // Replace \u00ed with í
        .replace(/\\u00f8/g, 'ø')
        .replace(/&amp;/g, '&')
        .replace(/\\u[0-9a-fA-F]{4}/g, '') // Remove any other Unicode escapes
        .trim();
    };

    authors = JSON.parse(authors);

    return authors
      .map(author => cleanName(author))
      .join('; ');
  
  };


  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleSearch = (key, value) => {
    setSearchTerms(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const toggleColumn = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const exportToCSV = () => {
    if (!data) return;
    
    const headers = ['pubId', 'doi', 'title', 'authors', 'journal', 'year']
      .map(field => field === 'pubId' ? 'PUBLICATION ID' : field.toUpperCase())
      .join(',');

    const rows = filteredAndSortedPublications.map(pub => 
      ['pubId', 'doi', 'title', 'authors', 'journal', 'year']
        .map(field => `"${pub[field]?.toString().replace(/,/g, ';') || ''}"`)
        .join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'publications.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredAndSortedPublications = useMemo(() => {
    if (!data) return [];
    let filteredItems = data.publicationsExp.edges.map(edge => edge.node);

    // Apply filters
    filteredItems = filteredItems.filter(item => {
      return (
        (filters.year === '' || item.year.toString() === filters.year) &&
        (filters.journal === '' || item.journal.toLowerCase().includes(filters.journal.toLowerCase())) &&
        (filters.pubId === '' || item.pubId.toString().toLowerCase().includes(filters.pubId.toLowerCase()))
      );
    });

    // Sort items
    if (sortConfig.key !== null) {
      filteredItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredItems;
  }, [data, sortConfig, filters]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedPublications.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;


  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-white border-b border-gray-200">
        <div className=" px-4">
          <div className="flex justify-between items-center py-2">
            {/* Moved Header to top left side */}
            <div className="px-4 py-2 flex justify-between items-center">
              <Header />
            </div>
            {/* Moved  Search to top right side */}
            <div className="flex items-center w-1/6">
              <div className="relative flex items-center  w-full">
                <input
                  type="text"
                  placeholder="Search publications..."
                  value={filters.pubId}
                  onChange={(e) => handleFilterChange('pubId', e.target.value)}
                  className="w-full p-2 pr-12 border rounded-md shadow-sm"
                />
                <button
                  className="absolute right-0 h-full px-4 bg-red-800 text-white rounded-r-md hover:bg-red-900"
                >
                  Q
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="flex-grow">
        <div className="container mx-auto p-4">
          <h1 className="text-4xl font-bold my-10 text-center text-black">Publications</h1>

          {/* Filters and Action Buttons */}
          <div className="mb-4 flex justify-between">
            <div className="flex space-x-4">
              <input
                type="text"
                placeholder="Filter by year"
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                className="p-2 border rounded"
              />
              <input
                type="text"
                placeholder="Filter by journal"
                value={filters.journal}
                onChange={(e) => handleFilterChange('journal', e.target.value)}
                className="p-2 border rounded"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-4">
              {/* Columns Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
                  className="bg-red-800 text-white px-4 py-2 rounded-md flex items-center"
                >
                  <FaColumns className="mr-2" />
                  Columns
                </button>
                {showColumnsDropdown && (
                  <div className="absolute right-0 z-10 mt-2 w-48 bg-white rounded-md shadow-lg">
                    {Object.keys(visibleColumns).map(columnName => (
                      <label key={columnName} className="flex items-center px-4 py-2 hover:bg-gray-100">
                        <input
                          type="checkbox"
                          checked={visibleColumns[columnName]}
                          onChange={() => toggleColumn(columnName)}
                          className="mr-2"
                        />
                        {columnName === 'pubId' ? 'Publication ID' : 
                         columnName === 'doi' ? 'DOI' : 
                         columnName.charAt(0).toUpperCase() + columnName.slice(1)}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Export Button */}
              <button
                onClick={exportToCSV}
                className="bg-red-800 text-white px-4 py-2 rounded-md flex items-center"
              >
                <FaFileExport className="mr-2" />
                Export Table
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-red-800 text-white uppercase text-sm leading-normal">
                  {['pubId', 'doi', 'title', 'authors', 'journal', 'year'].map(key => (
                    <th key={key} className="py-3 px-6 text-left">
                      <button onClick={() => handleSort(key)} className="font-bold w-full flex justify-center items-center">
                        {key === 'pubId' ? 'PUBLICATION ID' :
                         key === 'doi' ? 'DOI' :
                         key.toUpperCase()}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-black text-sm font-light">
                {currentItems.map((pub) => (
                  <tr key={pub.pubId} className="border-b border-gray-200 hover:bg-gray-100">
                    <td className="py-3 px-6 text-left whitespace-nowrap">
                      <Link to={`/publications/${pub.pubId}`} className="font-medium text-blue-800 hover:underline">
                        {pub.pubId}
                      </Link>
                    </td>
                    <td className="py-3 px-6 text-left">
                      {pub.doi && (
                        <a
                          href={`https://dx.doi.org/${pub.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs font-medium rounded-md overflow-hidden"
                        >
                          <span className="bg-gray-800 text-white px-2 py-1">DOI</span>
                          <span className="bg-blue-100 text-blue-800 px-2 py-1">{pub.doi}</span>
                        </a>
                      )}
                    </td>
                    <td className="py-3 px-6 text-left">{pub.title}</td>
                    <td className="py-3 px-6 text-left">{formatAuthors(pub.authors)}</td>
                    <td className="py-3 px-6 text-left">{pub.journal}</td>
                    <td className="py-3 px-6 text-left">{pub.year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-4 mb-8">
            {Array.from({ length: Math.ceil(filteredAndSortedPublications.length / itemsPerPage) }).map((_, index) => (
              <button
                key={index}
                onClick={() => paginate(index + 1)}
                className={`mx-1 px-3 py-1 rounded ${currentPage === index + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
      

      {/* Footer */}
      <footer className="bg-red-800 text-white py-4">
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src="/images/suncat_logo.png" 
            alt="suncat_logo.png" 
            className="h-8 mr-4"
          />
        </div>
        <nav>
          <ul className="flex space-x-6 items-center">
            <li>
              <a href="#" className="flex items-center hover:underline">
                <FaBook className="mr-2" /> Docs
              </a>
            </li>
            <li>
              <a href="#" className="flex items-center hover:underline">
                <FaGithub className="mr-2" /> Visit us on GitHub
              </a>
            </li>
            <li>
              <a href="#" className="flex items-center hover:underline">
                <FaCreativeCommons className="mr-2" /> License
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </footer>
    </div>
  );
};

export default PublicationsPage;